import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { academyAdminAPI } from '../../lib/academyApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Calendar, X, Users, Video, Link } from 'lucide-react';

const statusColors = { open: 'bg-green-100 text-green-700', upcoming: 'bg-blue-100 text-blue-700', full: 'bg-orange-100 text-orange-700', completed: 'bg-neutral-100 text-neutral-500', cancelled: 'bg-red-100 text-red-700' };
const statusLabels = { open: 'Ouvert', upcoming: 'A venir', full: 'Complet', completed: 'Termine', cancelled: 'Annule' };

const AcademySessions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ course_id: '', session_title: '', start_date: '', end_date: '', teams_link: '', max_participants: 20, status: 'open', notes: '' });

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [sRes, tRes] = await Promise.all([academyAdminAPI.listSessions(), academyAdminAPI.listTrainings()]);
      setSessions(sRes.data);
      setTrainings(tRes.data);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const openNew = () => { setEditing(null); setForm({ course_id: trainings[0]?.id || '', session_title: '', start_date: '', end_date: '', teams_link: '', max_participants: 20, status: 'open', notes: '' }); setShowForm(true); };
  const openEdit = (s) => { setEditing(s.id); setForm({ course_id: s.course_id, session_title: s.session_title || '', start_date: s.start_date || '', end_date: s.end_date || '', teams_link: s.teams_link || '', max_participants: s.max_participants || 20, status: s.status || 'open', notes: s.notes || '' }); setShowForm(true); };

  const handleSave = async () => {
    if (!form.course_id || !form.start_date) { toast.error('Formation et date de debut requises'); return; }
    try {
      if (editing) { await academyAdminAPI.updateSession(editing, form); toast.success('Session modifiee'); }
      else { await academyAdminAPI.createSession(form); toast.success('Session creee'); }
      setShowForm(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette session?')) return;
    try { await academyAdminAPI.deleteSession(id); toast.success('Session supprimee'); fetchData(); }
    catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#F9B438]" /></div>;

  return (
    <div className="space-y-6" data-testid="academy-sessions-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-heading font-bold text-[#1D1D1B]">Sessions de formation</h1><p className="text-sm text-neutral-500 mt-1">{sessions.length} session(s)</p></div>
        <Button onClick={openNew} data-testid="add-session-btn"><Plus className="w-4 h-4 mr-2" />Nouvelle session</Button>
      </div>

      {sessions.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><Calendar className="w-10 h-10 text-neutral-200 mx-auto mb-3" /><p className="text-neutral-400">Aucune session planifiee</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {sessions.map(s => (
            <Card key={s.id} className="hover:shadow-sm transition-shadow" data-testid={`session-${s.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.15) 0%, rgba(249,180,56,0.15) 100%)' }}>
                  <Calendar className="w-6 h-6 text-[#F9B438]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-[#1D1D1B] truncate">{s.course_title}</h3>
                  {s.session_title && <p className="text-xs text-neutral-400">{s.session_title}</p>}
                  <div className="flex items-center gap-3 mt-1 text-xs text-neutral-500">
                    <span>{s.start_date}{s.end_date && s.end_date !== s.start_date ? ` au ${s.end_date}` : ''}</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{s.registered_count || 0}/{s.max_participants}</span>
                    {s.teams_link && <span className="flex items-center gap-1"><Video className="w-3 h-3 text-blue-500" />Teams</span>}
                  </div>
                </div>
                <Badge className={statusColors[s.status] || 'bg-neutral-100'}>{statusLabels[s.status] || s.status}</Badge>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(s)}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => handleDelete(s.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowForm(false)} />
          <Card className="relative w-full max-w-lg z-10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editing ? 'Modifier' : 'Nouvelle'} session</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Formation *</Label>
                <select className="w-full h-10 px-3 rounded-md border bg-background text-sm" value={form.course_id} onChange={e => setForm({...form, course_id: e.target.value})} data-testid="session-course-select">
                  {trainings.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              </div>
              <div><Label>Titre de la session</Label><Input value={form.session_title} onChange={e => setForm({...form, session_title: e.target.value})} placeholder="Ex: Session de mai 2026" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Date debut *</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} /></div>
                <div><Label>Date fin</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} /></div>
              </div>
              <div><Label>Lien Microsoft Teams</Label><Input value={form.teams_link} onChange={e => setForm({...form, teams_link: e.target.value})} placeholder="https://teams.microsoft.com/..." /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Places max</Label><Input type="number" value={form.max_participants} onChange={e => setForm({...form, max_participants: parseInt(e.target.value) || 20})} /></div>
                <div><Label>Statut</Label><select className="w-full h-10 px-3 rounded-md border bg-background text-sm" value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                  <option value="open">Ouvert</option><option value="upcoming">A venir</option><option value="full">Complet</option><option value="completed">Termine</option><option value="cancelled">Annule</option>
                </select></div>
              </div>
              <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">Annuler</Button>
                <Button onClick={handleSave} className="flex-1" data-testid="save-session-btn">{editing ? 'Enregistrer' : 'Creer'}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AcademySessions;
